import { useEffect, useState } from "react";
import { X, Save, Loader2, Sparkles, Trash2 } from "lucide-react";
import { adminApi } from "@/lib/adminApi";
import { toast } from "sonner";
import ItemList from "./ItemList";

/**
 * DayDrawer — slide-in panel for editing a single day's tasks + note + recurring.
 */
export default function DayDrawer({ project_slug, date, open, onClose }) {
  const [day, setDay] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);

  useEffect(() => {
    if (!open || !date) return undefined;
    let alive = true;
    setDay(null);
    adminApi.getDay(project_slug, date).then((r) => {
      if (!alive) return;
      setDay(r.data);
      setNoteText(r.data.note?.text || "");
    });
    return () => {
      alive = false;
    };
  }, [project_slug, date, open]);

  const onSaveNote = async () => {
    setSaving(true);
    try {
      await adminApi.putNote(project_slug, date, noteText);
      toast.success("Saved");
    } catch (e) {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  const onClearNote = async () => {
    setSaving(true);
    try {
      await adminApi.putNote(project_slug, date, "");
      setNoteText("");
      setConfirmClear(false);
      toast.success("Diary cleared");
    } catch (e) {
      toast.error("Could not clear");
    } finally {
      setSaving(false);
    }
  };

  const toggleRec = async (r) => {
    const newDone = !r.done_today;
    setDay({
      ...day,
      recurring: day.recurring.map((x) =>
        x.id === r.id ? { ...x, done_today: newDone } : x
      ),
    });
    await adminApi.toggleRecurring(r.id, date, newDone).catch(() => {
      // revert
      setDay({
        ...day,
        recurring: day.recurring.map((x) =>
          x.id === r.id ? { ...x, done_today: !newDone } : x
        ),
      });
    });
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="day-drawer">
      {/* backdrop */}
      <div
        className="flex-1 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        data-testid="day-drawer-backdrop"
      />
      {/* panel */}
      <div className="w-full max-w-xl bg-ink text-parchment border-l border-parchment-faint overflow-y-auto">
        <div className="sticky top-0 bg-ink border-b border-parchment-faint px-6 py-4 flex items-center justify-between z-10">
          <div>
            <p className="text-xs tracking-widest opacity-60">DAY</p>
            <h2 className="font-serif text-2xl">{date}</h2>
          </div>
          <button onClick={onClose} aria-label="close" data-testid="day-drawer-close">
            <X size={20} />
          </button>
        </div>

        <div className="px-6 py-6 space-y-10">
          {!day && (
            <div className="opacity-60 text-xs flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" /> loading…
            </div>
          )}

          {day && (
            <>
              {/* tasks */}
              <ItemList
                kind="task"
                project_slug={project_slug}
                scope="day"
                scope_key={date}
                title="TASKS"
              />

              {/* recurring habits */}
              {day.recurring.length > 0 && (
                <div data-testid="day-recurring">
                  <p className="text-xs tracking-widest opacity-60 mb-3 text-four-yellow">
                    HABITS TODAY
                  </p>
                  <ul className="space-y-2">
                    {day.recurring.map((r) => (
                      <li key={r.id} className="flex items-center gap-3">
                        <button
                          onClick={() => toggleRec(r)}
                          className={`w-4 h-4 border flex-shrink-0 transition-colors ${
                            r.done_today
                              ? "border-four-yellow bg-four-yellow"
                              : "border-parchment-faint hover:border-four-yellow"
                          }`}
                          data-testid={`habit-toggle-${r.id}`}
                          aria-label="toggle habit"
                        />
                        <span className={`text-sm ${r.done_today ? "line-through opacity-40" : ""}`}>
                          {r.text}
                        </span>
                        <span className="text-[10px] opacity-40 tracking-widest ml-auto">
                          {r.cadence === "daily" ? "DAILY" : "WEEKLY"}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* diary note */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs tracking-widest opacity-60 text-four-yellow">DIARY</p>
                  <div className="flex items-center gap-3">
                    {noteText && (
                      confirmClear ? (
                        <>
                          <button
                            onClick={onClearNote}
                            className="text-xs tracking-widest text-red-400 hover:text-red-300"
                            data-testid="day-note-clear-confirm"
                          >
                            CLEAR?
                          </button>
                          <button
                            onClick={() => setConfirmClear(false)}
                            className="opacity-60 hover:opacity-100"
                            aria-label="cancel clear"
                          >
                            <X size={12} />
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => setConfirmClear(true)}
                          className="flex items-center gap-2 text-xs tracking-widest opacity-50 hover:opacity-100 hover:text-red-400 transition-colors"
                          data-testid="day-note-clear"
                          title="Clear diary"
                        >
                          <Trash2 size={12} />
                          CLEAR
                        </button>
                      )
                    )}
                    <button
                      onClick={onSaveNote}
                      disabled={saving}
                      className="flex items-center gap-2 text-xs tracking-widest opacity-60 hover:opacity-100 hover:text-four-yellow transition-colors"
                      data-testid="day-note-save"
                    >
                      {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                      SAVE
                    </button>
                  </div>
                </div>
                <textarea
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="What did I accomplish, what slipped, what's the lesson?"
                  className="w-full min-h-[200px] bg-transparent border border-parchment-faint focus:border-four-yellow outline-none text-sm p-3 font-sans transition-colors placeholder:opacity-30"
                  data-testid="day-note-textarea"
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
